// Components/modals/RescheduleModal.jsx
import React, { useState, useEffect } from 'react';
import { Form, Button, Spinner } from 'react-bootstrap';
import dayjs from 'dayjs';
import { GenericModal } from './GenericModal';

export const SessionRescheduleModal = ({ 
    show, 
    handleClose, 
    onSuccess, 
    initialDate, 
    initialStartTime, 
    initialEndTime,
    isProcessing = false
}) => {
    const [newSessionDate, setNewSessionDate] = useState(initialDate || '');
    const [newSessionStartTime, setNewSessionStartTime] = useState(initialStartTime || '');
    const [newSessionEndTime, setNewSessionEndTime] = useState(initialEndTime || '');
    const [errors, setErrors] = useState({
        date: '',
        startTime: '',
        endTime: ''
    });

    // Reset form when modal opens with new initial values
    useEffect(() => {
        if (show) {
            setNewSessionDate(initialDate || '');
            setNewSessionStartTime(initialStartTime || '');
            setNewSessionEndTime(initialEndTime || '');
        }
    }, [show, initialDate, initialStartTime, initialEndTime]);

    // Reset form errors when modal closes
    useEffect(() => {
        if (!show) {
            setErrors({
                date: '',
                startTime: '',
                endTime: ''
            });
        }
    }, [show]);

    const validateForm = () => {
        let isValid = true;
        const newErrors = {
            date: '',
            startTime: '',
            endTime: ''
        };

        // Date validation
        if (!newSessionDate) {
            newErrors.date = 'Date is required';
            isValid = false;
        } else {
            const selectedDate = dayjs(newSessionDate);
            const today = dayjs().startOf('day');
            
            if (selectedDate.isBefore(today)) {
                newErrors.date = 'Cannot schedule sessions in the past';
                isValid = false;
            }
        }

        // Time validation
        if (!newSessionStartTime) {
            newErrors.startTime = 'Start time is required';
            isValid = false;
        }

        if (!newSessionEndTime) {
            newErrors.endTime = 'End time is required';
            isValid = false;
        }

        if (newSessionStartTime && newSessionEndTime) {
            const startTime = dayjs(`${newSessionDate} ${newSessionStartTime}`);
            const endTime = dayjs(`${newSessionDate} ${newSessionEndTime}`);
            
            if (startTime.isAfter(endTime) || startTime.isSame(endTime)) {
                newErrors.endTime = 'End time must be after start time';
                isValid = false;
            }
            
            // Check if session is in the past
            const now = dayjs();
            if (startTime.isBefore(now) && newSessionDate === dayjs().format('YYYY-MM-DD')) {
                newErrors.startTime = 'Cannot schedule sessions in the past';
                isValid = false;
            }
            
            // Check if session duration is reasonable (e.g., not too long)
            const durationInMinutes = endTime.diff(startTime, 'minute');
            if (durationInMinutes > 240) { // 4 hours max
                newErrors.endTime = 'Session cannot be longer than 4 hours';
                isValid = false;
            }
            
            if (durationInMinutes < 15) { // 15 minutes min
                newErrors.endTime = 'Session must be at least 15 minutes';
                isValid = false;
            }
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleRescheduleClick = () => {
        if (!validateForm()) {
            return;
        }
    
        try {
            // Create dayjs objects from the date and time inputs
            const startDateTime = dayjs(`${newSessionDate} ${newSessionStartTime}`);
            const endDateTime = dayjs(`${newSessionDate} ${newSessionEndTime}`);
            const duration = endDateTime.diff(startDateTime, 'minute');
            
            onSuccess(startDateTime, duration);
        } catch (error) {
            console.error('Failed to reschedule session:', error);
        }
    };

    return (
        <GenericModal
            show={show}
            handleClose={isProcessing ? undefined : handleClose}
            title="Reschedule Session"
            footerActions={
                <>
                    <Button 
                        variant="secondary" 
                        onClick={handleClose}
                        disabled={isProcessing}
                    >
                        Close
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleRescheduleClick}
                        disabled={isProcessing}
                    >
                        {isProcessing ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Rescheduling...
                            </>
                        ) : (
                            'Reschedule Session'
                        )}
                    </Button>
                </>
            }
        >
            <p>Are you sure you want to reschedule this session?</p>
            <p>Choose a new date and time for the session.</p>
            <Form>
                <Form.Group className="mb-3">
                    <Form.Label>New Date <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                        type="date"
                        value={newSessionDate}
                        onChange={(e) => setNewSessionDate(e.target.value)}
                        isInvalid={!!errors.date}
                        disabled={isProcessing}
                        min={dayjs().format('YYYY-MM-DD')}
                    />
                    <Form.Control.Feedback type="invalid">
                        {errors.date}
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label>Start Time <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                        type="time"
                        value={newSessionStartTime}
                        onChange={(e) => setNewSessionStartTime(e.target.value)}
                        isInvalid={!!errors.startTime}
                        disabled={isProcessing}
                    />
                    <Form.Control.Feedback type="invalid">
                        {errors.startTime}
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label>End Time <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                        type="time"
                        value={newSessionEndTime}
                        onChange={(e) => setNewSessionEndTime(e.target.value)}
                        isInvalid={!!errors.endTime}
                        disabled={isProcessing}
                    />
                    <Form.Control.Feedback type="invalid">
                        {errors.endTime}
                    </Form.Control.Feedback>
                </Form.Group>
            </Form>
        </GenericModal>
    );
};